import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import WebRoundedIcon from '@mui/icons-material/WebRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import {
    Stack,
    useTheme
} from "@mui/material";
import whiteLogo from "../assets/logos/DinoBooking_logo_oldalt_felirat-04.png"
import {NavLink} from "react-router";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import {useContext, useState} from "react";
import {AuthContext} from "../context/AuthContext";
import DinoDialog from "./Dialog";
import packageJson from '../../package.json'
import {truncateText} from "../utils/misc";
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import {hasPermission} from "../utils/permissionUtils";
import SubscriptionContext from "../context/SubscriptionContext";
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';

const drawerWidth = 240;

function ResponsiveDrawer(props) {
    const {w} = props;
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
    const {getUser} = useContext(AuthContext);
    const {subscriptionInfo} = useContext(SubscriptionContext);
    const user = getUser()
    const menuItems = [
        {name: 'Vezérlőpult', url: '/', icon: <DashboardRoundedIcon/>, canView: hasPermission(user, 'reservation', 'view')},
        {name: 'Foglalások', url: '/reservations', icon: <CalendarMonthRoundedIcon/>, canView: hasPermission(user, 'reservation', 'view')},
        {name: 'Megrendelések', url: '/orders', icon: <LocalShippingRoundedIcon/>, canView: hasPermission(user, 'orders', 'view')},
        {name: 'Kategóriák', url: '/categories', icon: <SortRoundedIcon/>, canView: hasPermission(user, 'categories', 'view')},
        {name: 'Termékek', url: '/extra-items', icon: <ShoppingBagRoundedIcon/>, canView: hasPermission(user, 'extra-items', 'view')},
        {name: 'Kollégák', url: '/colleagues', icon: <PeopleRoundedIcon/>, canView: hasPermission(user, 'colleagues', 'view')},
        {name: 'Weboldal', url: '/web', icon: <WebRoundedIcon/>, canView: hasPermission(user, 'web', 'view')},
        {name: 'Nyitvatartás', url: '/opening-hours', icon: <CalendarTodayRoundedIcon/>, canView: hasPermission(user, 'opening-hours', 'view')},
        {name: 'Értesítések', url: '/notifications', icon: <NotificationsRoundedIcon/>, canView: hasPermission(user, 'notifications', 'view')},
        {name: 'Beállítások', url: '/settings', icon: <SettingsRoundedIcon/>, canView: hasPermission(user, 'settings', 'view')},
        {name: 'Varázsló', url: '/wizard', icon: <AutoFixHighRoundedIcon/>, canView: hasPermission(user, 'settings', 'view')},
    ]
    const theme = useTheme()
    const {logout} = useContext(AuthContext)

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const drawer = (
        <div
            style={{
                background: theme.palette.primary[500],
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Header */}
            <Toolbar style={{ background: theme.palette.primary[600], flexShrink: 0 }}>
                <img src={whiteLogo} alt="Dinobooking logo" width="100px" />
            </Toolbar>

            <Divider />

            {/* Scrollable menu */}
            <List
                sx={{
                    padding: '32px 16px',
                    flex: 1,
                    overflowY: 'auto',
                }}
            >
                {menuItems
                    .filter(item => item.canView)
                    .map(item => (
                        <ListItem
                            key={item.name}
                            className="menu-item"
                            id={`menu-item-${item.name}`}
                            disablePadding
                            sx={{ paddingBottom: '3px' }}
                        >
                            <NavLink
                                to={item.url}
                                className={({ isActive }) =>
                                    isActive ? 'menu-button__active' : 'menu-button'
                                }
                            >
                                <ListItemIcon className="menu-button__icon">
                                    {item.icon}
                                </ListItemIcon>
                                <Typography
                                    variant="button"
                                    className="menu-button__text"
                                >
                                    {item.name}
                                </Typography>
                            </NavLink>
                        </ListItem>
                    ))}
            </List>

            <Divider />

            {/* Footer */}
            <div
                style={{
                    padding: '8px 0 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ color: '#fff', mb: 1 }}
                >
                    v. {packageJson.version}
                </Typography>

                <Button
                    variant="text"
                    sx={{
                        color: '#fff',
                        '&:hover': { background: 'transparent' },
                    }}
                    startIcon={<LogoutRoundedIcon />}
                    onClick={() => setLogoutDialogOpen(true)}
                >
                    kijelentkezés
                </Button>
            </div>
        </div>
    );


    const container = w !== undefined ? () => w().document.body : undefined;

    const logoutActions = <>
        <Button variant='outlined' style={{padding: 15}} onClick={() => setLogoutDialogOpen(false)}>Mégsem</Button>
        <Button variant='contained' style={{padding: 15}} onClick={() => {
            setLogoutDialogOpen(false)
            logout()
        }} endIcon={<LogoutRoundedIcon/>}>Kijelentkezés</Button>
    </>

    return (
        <Box sx={{display: 'flex'}}>
            <CssBaseline/>
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    background: {
                        xs: theme.palette.primary[600],
                        sm: theme.palette.primary[600],
                        md: '#fff',
                    },
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* LEFT: Title + filters (desktop) */}
                        <Stack
                            spacing={1}
                            sx={{
                                display: { xs: 'none', sm: 'none', md: 'flex' },
                                paddingBottom: '5px',
                                paddingTop: '5px',
                            }}
                        >
                            <Typography
                                variant="headline2"
                                noWrap
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                }}
                            >
                                {truncateText(props.label, 20)}
                            </Typography>

                            <Stack direction="row" spacing={2}>
                                {props.filterButtons?.map((button, i) => (
                                    <Button
                                        key={`f_button_${i}`}
                                        {...button.props}
                                    >
                                        {button.name}
                                    </Button>
                                ))}
                            </Stack>
                        </Stack>

                        {/* MOBILE LOGO */}
                        <Box
                            sx={{
                                flexGrow: 0,
                                display: { xs: 'flex', sm: 'flex', md: 'none' },
                            }}
                        >
                            <img src={whiteLogo} alt="Dinobooking logo" width="100px" />
                        </Box>

                        {/* CENTER SPACER */}
                        <Box sx={{ flexGrow: 1 }} />

                        {/* RIGHT: Independent text (desktop) */}
                        {subscriptionInfo && (
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    alignItems: 'center',
                                    mr: 2,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    {subscriptionInfo?.is_trial && 'Próbaidőszak! '}
                                    {subscriptionInfo?.subscription_end && 'Fordulónap: ' + subscriptionInfo?.subscription_end}
                                </Typography>
                            </Box>
                        )}

                        {/* RIGHT: Buttons (desktop) */}
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                display: { xs: 'none', sm: 'none', md: 'flex' },
                                alignItems: 'center',
                            }}
                        >
                            {props.buttons?.map((button, i) => (
                                <Button
                                    key={`menu_button_${i}`}
                                    {...button.props}
                                >
                                    {button.name}
                                </Button>
                            ))}
                        </Stack>

                        {/* MOBILE MENU BUTTON */}
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                marginLeft: 'auto',
                                zIndex: 11,
                                color: 'white'
                            }}
                        >
                            <IconButton
                                size="large"
                                onClick={handleDrawerToggle}
                                sx={{ color: 'inherit' }}
                            >
                                {!mobileOpen ? <MenuIcon /> : <CloseIcon />}
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>

                {/* MOBILE SECOND ROW */}
                <Stack
                    spacing={1}
                    padding={1}
                    direction="row"
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: '#fff',
                    }}
                >
                    <Typography variant="headline2">
                        {truncateText(props.label, 20)}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Independent text (mobile) */}
                        {/*{subscriptionInfo && (*/}
                        {/*    <Typography variant="body2">*/}
                        {/*        {subscriptionInfo?.is_trial && 'Próbaidőszak!'}*/}
                        {/*        {subscriptionInfo?.subscription_end && 'Fordulónap: ' + subscriptionInfo?.subscription_end}*/}
                        {/*    </Typography>*/}
                        {/*)}*/}

                        {props.filterButtonsMobile?.map((button, i) => (
                            <Button
                                key={`f_button_mobile_${i}`}
                                {...button.props}
                            >
                                {button.name}
                            </Button>
                        ))}
                    </Stack>
                </Stack>
            </AppBar>

            <Box
                component="nav"
                sx={{width: {md: drawerWidth}, flexShrink: {sm: 0}}}
                aria-label="mailbox folders"
            >
                {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: {sm: 'block', md: 'none'},
                        '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth},
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: {xs: 'none', sm: 'none', md: 'block'},
                        '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth},
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{flexGrow: 1, width: {sm: '100%', md: `calc(100% - ${drawerWidth}px)`}}}
            >
                {/*<Toolbar/>*/}

                {props?.filterButtonsMobile ? <Box sx={{height: 110, display: {xs: 'block', md: 'none'}}}/> :
                    <Box sx={{height: 70, display: {xs: 'block', md: 'none'}}}/>}
                {props.children}

                <DinoDialog open={logoutDialogOpen}
                            handleClose={() => setLogoutDialogOpen(false)}
                            title='Kijelentkezés'
                            subtitle='Biztosan kijelentkezik?'
                            actions={logoutActions}>

                </DinoDialog>
            </Box>
        </Box>
    );
}

export default ResponsiveDrawer;
